import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { BluechatLoad } from '../shared/bluechat-load.model';
import { BluechatSearchCriteria } from '../shared/bluechat-search-criteria.model';
import { LoadManagementService } from '../shared/load-management.service';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { ToastrService } from 'ngx-toastr';
import { NavigationService } from '../../shared/services/navigation.service';
import { CarrierService } from '../../user-dashboard/shared/carrier.service';
import { TranslateService } from '@ngx-translate/core';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { delay, switchMap } from 'rxjs/operators';
import { ModalService } from '../../shared/services/modal.service';
import { LoaderService } from '../../shared/services/loader.service';
import {
  LoadManagementSearchCarrierDto,
  LoadManagementSearchRegionDto,
  LoadManagementSearchUnknownCarrierDto
} from '../shared/load-management-search-response';
import { BluechatService } from '../bluechat.service';
import { BluechatCompany } from '../models/BluechatCompany';
import { BluechatProcess } from '../models/BluechatProcess';
import { forkJoin, Observable, of } from 'rxjs';
import { BluechatNoteModalDelivery } from '../models/BluechatNoteModalDelivery';
import { ActivatedRoute, Router } from '@angular/router';

export interface UserBluechatConfiguration {
  assignedCompanies: BluechatCompany[];
  assignedProcesses: BluechatProcess[];
}

@Component({
  selector: 'app-bluechat',
  templateUrl: './bluechat-search.component.html',
  styleUrls: ['./bluechat-search.component.scss']
})
export class BluechatSearchComponent implements OnInit {

  @ViewChild('noteCreationModal') public noteCreationModal: TemplateRef<any>;
  @ViewChild('loadDetailModal') public loadDetailModal: TemplateRef<any>;
  public filters                            = new BluechatSearchCriteria();
  public bluechatLoads: Array<BluechatLoad>;
  public bluechatDeliveriesWithUnknownCarrier: Array<{ carrierName: string, deliveryNumbers: Array<string> }>;
  public seeAllCarriers                     = false;
  public displayHeaderRightWarning          = false;
  public selectedLoads: Array<BluechatLoad> = [];
  public noteLoads: BluechatLoad[] = [];
  public noteDeliveries: BluechatNoteModalDelivery[] = [];
  public selectedDeliveriesNumber           = 0;
  public userHasBluechatEnabled             = false;

  @ViewChild('massCreationModal') private massCreationModal: TemplateRef<any>;
  public knownCarriers: LoadManagementSearchCarrierDto[] = [];
  public knownCarriersByBlueyonderCode: { [carrierCode: string]: LoadManagementSearchCarrierDto } = {};
  public knownRegionsById: { [regionId: string]: LoadManagementSearchRegionDto } = {};
  public unknownCarriers: LoadManagementSearchUnknownCarrierDto[] = [];
  public userBluechatConfig: UserBluechatConfiguration            = {
    assignedCompanies: [],
    assignedProcesses: [],
  };

  constructor(
    private authService: AuthenticationService,
    private toastrService: ToastrService,
    private navigationService: NavigationService,
    private loadManagementService: LoadManagementService,
    private carrierService: CarrierService,
    private translateService: TranslateService,
    private errorHandlerService: ErrorHandlerService,
    private modalService: ModalService,
    private loaderService: LoaderService,
    private bluechatService: BluechatService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
  }

  public ngOnInit() {
    this.seeAllCarriers = this.authService.canViewAllLoadsManagement();
    this.displayHeaderRightWarning  = this.authService.canViewAllLoadsManagement() && !this.authService.canSubmitAllBluechatNotes();
    this.userHasBluechatEnabled = this.authService.canSubmitBluechat();

    if (this.authService.canSubmitAllBluechatNotes()) {
      forkJoin([this.bluechatService.getSelfAssignedCompanies(), this.bluechatService.getSelfAssignedProcesses()])
        .subscribe(([companies, processes]) => {
          this.userBluechatConfig = {
            assignedCompanies: companies,
            assignedProcesses: processes,
          }
      });
    } else if (this.authService.canSubmitCarrierBluechatNotes()) {
      const currentCarrier = this.authService.getUserProfile().carrier;
      forkJoin([
        this.bluechatService.getCompany(currentCarrier.bluechatCarrierCode),
        this.bluechatService.getProcesses([currentCarrier.bluechatCarrierCode])
      ]).subscribe(([company, processes]) => {
          this.userBluechatConfig = {
            assignedCompanies: [company],
            assignedProcesses: processes[currentCarrier.bluechatCarrierCode],
          }
        });
    }

    this.searchFromQuery();

    this.userHasBluechatEnabled = this.authService.canSubmitBluechat();
  }

  public search() {
    this.loaderService.startLoad();
    this.loadManagementService.search(this.filters)
      .subscribe(response => {
        this.selectedLoads            = [];
        this.selectedDeliveriesNumber = 0;
        this.knownCarriers = response.carriers;
        this.knownCarriersByBlueyonderCode = Object.fromEntries(response.carriers.map(c => [c.blueyonderCarrierCode, c]));
        this.knownRegionsById = Object.fromEntries(response.regions.map(r => [r.id, r]));
        this.unknownCarriers = response.unknownCarriers;
        if (this.seeAllCarriers) {
          this.setKnownAndUnknownCarriersBluechatLoads(response.loads);
        } else {
          this.bluechatLoads = response.loads;
        }
        this.loaderService.stopLoad();
      }, e => {
        this.errorHandlerService.handleHttpError(e, 'bluechat.search.error.fetching');
        this.loaderService.stopLoad();
      });

    this.changeQueryParamsTo(this.filters);
  }

  public reset() {
    this.filters                              = new BluechatSearchCriteria();
    this.bluechatLoads                        = [];
    this.bluechatDeliveriesWithUnknownCarrier = [];
    this.selectedLoads                        = [];
    this.selectedDeliveriesNumber             = 0;
    this.unknownCarriers                      = [];
  }

  public openBluechatNoteCreationModal(load: BluechatLoad) {
    this.noteDeliveries = load.deliveries.map(delivery => {
      const noteDelivery = new BluechatNoteModalDelivery();

      noteDelivery.deliveryNumber = delivery.deliveryNumber;
      noteDelivery.carrierName = load.carrierName;
      noteDelivery.carrierBlueyonderCode = load.blueyonderCarrierCode;
      noteDelivery.carrierBluechatCode = load.bluechatCarrierCode;
      noteDelivery.carrierHasBluechatEnabled = load.bluechatCarrierCode != null;
      noteDelivery.carrierExistsOnCmaWeb = load.blueyonderCarrierCode != null;

      return noteDelivery;
    });

    this.modalService.open({
      title: this.translateService.instant(
        'bluechat.note-creation.title-single-delivery',
        { deliveryNumber: this.noteDeliveries[0]?.deliveryNumber },
      ),
      closeButton: {
        displayed: false,
        text: null,
      },
      validateButton: {
        displayed: false,
        text: null,
      },
      content: this.noteCreationModal,
    }).subscribe();
  }

  public openBluechatMassCreationModal() {
    this.noteLoads = this.selectedLoads;
    this.modalService.open({
      title: this.translateService.instant('bluechat.mass-creation.title'),
      content: this.massCreationModal,
      validateButton: {
        displayed: false,
        text: null,
      }
    }).pipe(
      delay(10)
    ).subscribe(_ => {
      this.modalService.open({
        title: this.translateService.instant('bluechat.note-creation.title-multiple-deliveries'),
        closeButton: {
          displayed: false,
          text: null,
        },
        validateButton: {
          displayed: false,
          text: null,
        },
        content: this.noteCreationModal,
      }).subscribe();
    });
  }

  public updateSelectedLoads(selectedLoads: Array<BluechatLoad>) {
    this.selectedLoads            = selectedLoads;
    this.selectedDeliveriesNumber = selectedLoads.reduce(
      (currentNumberDeliveries, load) => currentNumberDeliveries + load.deliveries.length,
      0
    );
  }

  public goToLoadDetail(load: BluechatLoad) {
    this.navigationService.loadsManagement().detail(load.loadNumber).go()
  }

  public closeLoadDetailModal() {
    this.modalService.triggerResolution();
  }

  public createNoteFromDetailModal($event: BluechatLoad) {
    this.closeLoadDetailModal();
    this.openBluechatNoteCreationModal($event);
  }

  private setKnownAndUnknownCarriersBluechatLoads(bluechatLoads: Array<BluechatLoad>) {
    const unknownCarrierLoads: { [carrierCode: string]: Array<BluechatLoad> } = {};
    const knownCarrierLoads: Array<BluechatLoad>                              = [];

    for (const bluechatLoad of bluechatLoads) {
      if (this.knownCarriers.some(c => c.blueyonderCarrierCode === bluechatLoad.blueyonderCarrierCode)) {
        knownCarrierLoads.push(bluechatLoad);
      } else {
        if (unknownCarrierLoads.hasOwnProperty(bluechatLoad.blueyonderCarrierCode)) {
          unknownCarrierLoads[bluechatLoad.blueyonderCarrierCode] = [...unknownCarrierLoads[bluechatLoad.blueyonderCarrierCode], bluechatLoad]
        } else {
          unknownCarrierLoads[bluechatLoad.blueyonderCarrierCode] = [bluechatLoad]
        }
      }
    }

    this.bluechatLoads                        = knownCarrierLoads;
    this.bluechatDeliveriesWithUnknownCarrier = Object.values(unknownCarrierLoads)
      .map(loads => {
        const deliveryNumbers = loads
          .map(load => load.deliveries)
          .flat()
          .map(delivery => delivery.deliveryNumber)
        return { carrierName: loads[0].carrierName, deliveryNumbers };
      })
  }

  private changeQueryParamsTo(filters: BluechatSearchCriteria) {
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: filters
      }
    );
  }

  private searchFromQuery() {
    this.route.queryParams
      .subscribe(params => {

        this.assignFilterFromQuery(this.filters, params, 'deliveryNumbers');
        this.assignFilterFromQuery(this.filters, params, 'loadNumbers');

        if (this.filtersAreNotEmpty()) {
          this.search();
        }
      });
  }

  private assignFilterFromQuery(filterToAssign: any, params: any, paramKey: string) {
    if (params[paramKey] && Array.isArray(params[paramKey])) {
      filterToAssign[paramKey] = params[paramKey];
    } else {
      filterToAssign[paramKey] = params[paramKey] ? [params[paramKey]] : [];
    }
  }

  private thereIsSearchQueries(): Observable<boolean> {
    return this.route.queryParams.pipe(
      switchMap(params => of(params === {}))
    )
  }

  private filtersAreNotEmpty() {
    return (this.filters.deliveryNumbers && this.filters.deliveryNumbers.length > 0)
      || (this.filters.loadNumbers && this.filters.loadNumbers.length > 0);
  }
}
